import React, { useState, useEffect } from "react";
import { Button, Tabs, useMantineTheme } from "@mantine/core";
import { Navigate } from "react-router-dom";
import PageContainer from "../../../themes/components/PageContainer";
import PageHeading from "../../../themes/components/PageHeading";
import { AuthContext } from "../../../providers/AuthProvider";
import NotFound from "../../../common/pages/NotFound";
import { AccessPermissionContext } from "../../../providers/AccessPermissionsProvider";
import BlockedDatesTab from "../components/blocked-dates/BlockedDatesTab";
import FeatureFlags from "../../../common/feature-flagging/FeatureFlags";
import { useEnabledFeatureFlag } from "../../../common/feature-flagging/FeatureFlagProvider";
import UserAccessControlUtils, {
  FEATURE_SEGMENTS,
} from "../../../utils/UserAccessControlUtils";
import {
  OdpBlockedDatesProvider,
  OdpBlockedDatesContext,
} from "../providers/BlockedDatesProvider";
import { SegmentationBlocksProvider } from "../providers/SegmentationBlocksProvider";
import CreateNewBlockedDates from "../components/blocked-dates/CreateNewBlockedDates";
import {
  SegmentationBlocksTab,
  CreateSegmentationBlockDrawer,
  EditSegmentationBlockDrawer,
  SegmentationBlocksHistoryContent,
} from "../components/segmentation-blocks";
import { ISegmentationBlock } from "../@types/segmentationBlocks";
import CompanyLimitsTable from "../components/CompanyLimitsTable";
import useGetEmployerODPLimitsApiService from "../services/api/GetEmployerODPLimitsApiService";
import CompanyLimitsProvider from "../providers/CompanyLimitsProvider";
import ProgressIndicator from "../../../themes/components/ProgressIndicator";
import ErrorPage from "../../../common/pages/ErrorPage";
import { getHistoryButtonStyles } from "../../../common/styles/buttonStyles";

const CreateNewButton: React.FC<{
  showCreateNewBlockedDate: () => boolean;
  setCreateNewBlockedDateDialogOpen: (open: boolean) => void;
}> = ({ showCreateNewBlockedDate, setCreateNewBlockedDateDialogOpen }) => {
  const blockedDatesContext = React.useContext(OdpBlockedDatesContext);

  return (
    <Button
      onClick={() => setCreateNewBlockedDateDialogOpen(true)}
      variant="filled"
      type="button"
      color="jem-theme"
      disabled={blockedDatesContext?.isLoadingBlockedDates}
      className={`${showCreateNewBlockedDate() ? "visible" : "invisible"}`}
    >
      Create New
    </Button>
  );
};

const OdpLimitsPage: React.FC = () => {
  const { hasPermissions } = React.useContext(AccessPermissionContext);
  const auth = React.useContext(AuthContext);
  const theme = useMantineTheme();
  const [activeTab, setActiveTab] = useState<number>(
    parseInt(localStorage.getItem("odp-active-tab") ?? "", 10) || 0
  );
  const [createNewBlockedDateDialogOpen, setCreateNewBlockedDateDialogOpen] =
    useState<boolean>(false);
  const [createSegmentBlockDrawerOpen, setCreateSegmentBlockDrawerOpen] =
    useState<boolean>(false);
  const [editSegmentBlockDrawerOpen, setEditSegmentBlockDrawerOpen] =
    useState<boolean>(false);
  const [selectedBlock, setSelectedBlock] = useState<ISegmentationBlock | null>(
    null
  );
  const [showHistoryView, setShowHistoryView] = useState<boolean>(false);

  const handleEditBlock = (block: ISegmentationBlock): void => {
    setSelectedBlock(block);
    setEditSegmentBlockDrawerOpen(true);
  };

  const handleCloseEditDrawer = (): void => {
    setEditSegmentBlockDrawerOpen(false);
    setSelectedBlock(null);
  };

  const isBlockedDatesFeatureEnabled = useEnabledFeatureFlag(
    FeatureFlags.EMPLOYER_ODP_LIMITS_BLOCKED_DATES
  );
  const isTransactionalLimitsFeatureEnabled = useEnabledFeatureFlag(
    FeatureFlags.EMPLOYER_ODP_LIMITS_TRANSACTIONAL
  );
  const isSegmentationBlocksFeatureEnabled = useEnabledFeatureFlag(
    FeatureFlags.EMPLOYER_ODP_SEGMENTATION_BLOCKS
  );
  const hasSegmentsFeature = UserAccessControlUtils.hasFeature(
    auth?.user_features || [],
    FEATURE_SEGMENTS
  );

  const { isLoading, isError, error, data } =
    useGetEmployerODPLimitsApiService();

  useEffect(() => {
    localStorage.setItem("odp-active-tab", activeTab.toString());
  }, [activeTab]);

  if (!hasPermissions) {
    return <NotFound />;
  }

  if (!auth) {
    return <Navigate replace to="/login" />;
  }

  if (isLoading) {
    return <ProgressIndicator />;
  }

  if (isError) {
    return <ErrorPage errors={error} />;
  }

  const showCreateNewBlockedDate = (): boolean => {
    if (
      isBlockedDatesFeatureEnabled &&
      isTransactionalLimitsFeatureEnabled &&
      activeTab === 1
    )
      return true;
    if (isBlockedDatesFeatureEnabled && !isTransactionalLimitsFeatureEnabled)
      return true;
    return false;
  };

  const showCreateNewSegmentBlock = (): boolean => {
    // Calculate which tab index corresponds to segmentation blocks
    let segmentBlocksTabIndex = 0;
    if (isTransactionalLimitsFeatureEnabled) segmentBlocksTabIndex += 1;
    if (isBlockedDatesFeatureEnabled) segmentBlocksTabIndex += 1;

    return (
      isSegmentationBlocksFeatureEnabled &&
      hasSegmentsFeature &&
      activeTab === segmentBlocksTabIndex
    );
  };

  return (
    <PageContainer>
      <OdpBlockedDatesProvider>
        <SegmentationBlocksProvider>
          <PageHeading title="Financial Benefits">
            <CreateNewButton
              showCreateNewBlockedDate={showCreateNewBlockedDate}
              setCreateNewBlockedDateDialogOpen={
                setCreateNewBlockedDateDialogOpen
              }
            />
            {showCreateNewSegmentBlock() && !showHistoryView && (
              <>
                <Button
                  onClick={() => setShowHistoryView(true)}
                  variant="outline"
                  type="button"
                  styles={getHistoryButtonStyles(theme)}
                >
                  View History Updates
                </Button>
                <Button
                  onClick={() => setCreateSegmentBlockDrawerOpen(true)}
                  variant="filled"
                  type="button"
                  color="jem-theme"
                >
                  Create New
                </Button>
              </>
            )}
          </PageHeading>
          <Tabs
            mt={24}
            active={activeTab}
            onTabChange={(value) => {
              setActiveTab(value);
              localStorage.setItem("odp-active-tab", value.toString());
              setShowHistoryView(false);
            }}
            classNames={{
              body: "pt-4",
            }}
          >
            {isTransactionalLimitsFeatureEnabled && (
              <Tabs.Tab label="Limits" value="limits">
                {data && (
                  <CompanyLimitsProvider initialConfig={data}>
                    <CompanyLimitsTable />
                  </CompanyLimitsProvider>
                )}
              </Tabs.Tab>
            )}

            {isBlockedDatesFeatureEnabled && (
              <Tabs.Tab label="Blocked Dates" value="dates">
                <CreateNewBlockedDates
                  isOpen={createNewBlockedDateDialogOpen}
                  setIsOpen={setCreateNewBlockedDateDialogOpen}
                />
                <BlockedDatesTab />
              </Tabs.Tab>
            )}

            {isSegmentationBlocksFeatureEnabled && hasSegmentsFeature && (
              <Tabs.Tab label="Segmentation Blocks" value="segmentation">
                <CreateSegmentationBlockDrawer
                  isOpen={createSegmentBlockDrawerOpen}
                  setIsOpen={setCreateSegmentBlockDrawerOpen}
                />
                <EditSegmentationBlockDrawer
                  isOpen={editSegmentBlockDrawerOpen}
                  onClose={handleCloseEditDrawer}
                  block={selectedBlock}
                />
                {showHistoryView ? (
                  <SegmentationBlocksHistoryContent
                    onBackToOverview={() => setShowHistoryView(false)}
                  />
                ) : (
                  <SegmentationBlocksTab
                    onCreateNew={() => setCreateSegmentBlockDrawerOpen(true)}
                    onEditBlock={handleEditBlock}
                  />
                )}
              </Tabs.Tab>
            )}
          </Tabs>
        </SegmentationBlocksProvider>
      </OdpBlockedDatesProvider>
    </PageContainer>
  );
};

export default OdpLimitsPage;
